import {
  type AssetManifest,
  EQUIPMENT_MAX_LEVEL,
  type EquipmentComponent,
  type EquipmentInstanceWire,
  type EquipmentSlotView,
  levelMultiplierText,
  type UpgradeCurrency,
  type UpgradeQuoteWire,
} from '@kenocyber/shared';
import { useMemo } from 'react';
import { staticAssetUrl } from '../game/assets';
import { EquipmentAssembly } from './EquipmentAssembly';
import { rarityLabel, rarityPaint, rarityStyle } from './rarity';

/**
 * EQUIPAMENTOS: the five slots, drawn as the energy system they are.
 *
 * ## The layout
 *
 * Taken from the reference art, and structural rather than decorative: the
 * Núcleo sits at the centre, the Câmara is the ring around it, the Distribuidor
 * is the ring outside that, the Condutores are the three visible spokes, and
 * the Receptores are what the spokes reach. A player who has seen the diagram
 * can find a slot without reading a label.
 *
 * ## No invented art
 *
 * The forty-five components have no PNG yet, and none is invented here: a slot
 * draws a professional frame, its name and its state. When the art arrives it
 * fills `assetKey`, which is already read below, and nothing else changes.
 *
 * ## Rarity is the existing system
 *
 * The border, the glow and the label come from `rarityStyle` and `rarityPaint` -
 * the same two functions the inventory cells and the marketplace cards use,
 * which read `RARITY_THEME` in `@kenocyber/shared`. There is no second colour
 * table here and there must not be one: a component can never be painted a
 * colour the catalog does not know about.
 */

export interface EquipmentTabProps {
  readonly slots: readonly EquipmentSlotView[];
  readonly owned: readonly EquipmentInstanceWire[];
  readonly selectedId: string | null;
  /** The art manifest, so the assembly can resolve its five layers. */
  readonly manifest: AssetManifest;
  readonly onSelect: (itemId: string) => void;
  /** Selecting a SLOT rather than an item: shows what is in it, or that it is empty. */
  readonly onSelectSlot: (component: EquipmentComponent) => void;
  /**
   * Which component type the available list is restricted to, or null for all.
   *
   * Held above this component because it survives a selection being cleared:
   * clicking an EMPTY slot has no item to describe, and the type the player
   * asked about is exactly the thing that must not be forgotten at that moment.
   */
  readonly focus: EquipmentComponent | null;
  readonly onFocus: (component: EquipmentComponent | null) => void;
}

/** The order the legend lists them in, matching the reference art. */
const SLOT_ORDER: readonly EquipmentComponent[] = [
  'core',
  'chamber',
  'distributor',
  'conduits',
  'receptor',
];

export function EquipmentTab({
  slots,
  owned,
  selectedId,
  manifest,
  onSelect,
  onSelectSlot,
  focus,
  onFocus,
}: EquipmentTabProps) {
  const bySlot = useMemo(() => new Map(slots.map((slot) => [slot.component, slot])), [slots]);
  const unequipped = useMemo(() => owned.filter((item) => !item.equipped), [owned]);
  /** Which PART of the machine is lit: the slot the selected item lives in. */
  const selectedComponent =
    slots.find((slot) => slot.item?.itemId === selectedId)?.component ??
    owned.find((item) => item.itemId === selectedId)?.component ??
    null;
  /*
   * The list shows ONE type at a time once a type has been chosen.
   *
   * With five families and ten rarities the flat list is long enough that
   * finding the chamber a player just clicked on means scrolling past four
   * families that cannot go in that slot. The focus survives clearing the
   * selection, so an empty slot still narrows the list to the parts that fit
   * it - which is the only moment the filter really earns its place.
   */
  const active = focus ?? selectedComponent;
  const visible = useMemo(
    () => (active ? unequipped.filter((item) => item.component === active) : unequipped),
    [unequipped, active],
  );
  const activeLabel = active ? (bySlot.get(active)?.label ?? active) : null;

  return (
    <div className="equip-tab" data-testid="equipment-tab">
      {/*
        The machine itself: five aligned PNG layers, not five squares. Clicking a
        part selects it; an empty slot draws a hologram of what is missing.
      */}
      <EquipmentAssembly
        slots={slots}
        manifest={manifest}
        selected={active}
        onSelect={(component) => {
          onFocus(component);
          const item = bySlot.get(component)?.item;
          if (item) {
            onSelect(item.itemId);
          } else {
            onSelectSlot(component);
          }
        }}
      />

      {/*
        The legend. Five compact rows under the machine rather than five boxes
        around it: the diagram is the picture, and this says which is which
        without competing with it for space.
      */}
      <ul className="equip-legend" data-testid="equipment-legend">
        {SLOT_ORDER.map((component, index) => {
          const slot = bySlot.get(component);
          if (!slot) {
            return null;
          }
          const item = slot.item;
          return (
            <li key={component}>
              <button
                type="button"
                className={`equip-legend-row ${item ? '' : 'equip-legend-empty'}`}
                data-paint={item ? rarityPaint(item.rarity) : undefined}
                style={item ? rarityStyle(item.rarity) : undefined}
                data-testid={`equip-slot-${component}`}
                data-filled={item ? 'true' : 'false'}
                aria-pressed={item ? item.itemId === selectedId : active === component}
                onClick={() => {
                  onFocus(component);
                  if (item) {
                    onSelect(item.itemId);
                  } else {
                    onSelectSlot(component);
                  }
                }}
              >
                <span className="equip-legend-index" aria-hidden="true">
                  {index + 1}
                </span>
                <span className="equip-legend-name">{slot.label}</span>
                {item ? (
                  <>
                    <span className="equip-legend-rarity">{rarityLabel(item.rarity)}</span>
                    <span className="equip-legend-level">Nv. {item.level}</span>
                    <span className="equip-legend-power">
                      Power <span className="equip-power-value">{item.power}</span>
                    </span>
                  </>
                ) : (
                  <span className="equip-legend-state">Vazio</span>
                )}
              </button>
            </li>
          );
        })}
      </ul>

      <section className="equip-owned" aria-label="Equipamentos disponíveis">
        <div className="equip-owned-head">
          <h3>Disponíveis</h3>
          {activeLabel ? (
            <>
              <span className="equip-owned-filter" data-testid="equipment-filter">
                {activeLabel}
              </span>
              {/* The way back out. A filter with no visible exit is a dead end,
                and the player did not necessarily mean to set one. */}
              <button
                type="button"
                className="equip-owned-all"
                data-testid="equipment-filter-clear"
                onClick={() => onFocus(null)}
              >
                Ver todos
              </button>
            </>
          ) : null}
        </div>
        {visible.length === 0 ? (
          <p className="inv-state" data-testid="equipment-empty">
            {activeLabel
              ? `Nenhum componente do tipo ${activeLabel} disponível.`
              : 'Nenhum equipamento disponível.'}
          </p>
        ) : (
          <div className="equip-list" data-testid="equipment-list">
            {visible.map((item) => (
              <button
                key={item.itemId}
                type="button"
                className="equip-card"
                data-paint={rarityPaint(item.rarity)}
                style={rarityStyle(item.rarity)}
                data-testid={`equip-card-${item.itemId}`}
                aria-pressed={item.itemId === selectedId}
                onClick={() => onSelect(item.itemId)}
              >
                {/*
                  The icon, at 99% of the cell - the same rule every inventory
                  cell follows. A component the manifest has no art for draws
                  nothing rather than a broken image.
                */}
                <span className="equip-card-art" aria-hidden="true">
                  {staticAssetUrl(manifest, item.definitionId) ? (
                    <img
                      src={staticAssetUrl(manifest, item.definitionId) ?? undefined}
                      alt=""
                      loading="lazy"
                      draggable={false}
                    />
                  ) : null}
                </span>
                <span className="equip-card-name">{item.componentLabel}</span>
                <span className="equip-card-rarity">{rarityLabel(item.rarity)}</span>
                <span className="equip-card-level">Nv. {item.level}</span>
                <span className="equip-card-power">
                  Power <span className="equip-power-value">{item.power}</span>
                </span>
                {item.locked ? <span className="equip-card-badge">RESERVADO</span> : null}
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Details                                                                   */
/* -------------------------------------------------------------------------- */

export interface EquipmentDetailsProps {
  readonly item: EquipmentInstanceWire | null;
  /** The art manifest, so the panel can show the component it is describing. */
  readonly manifest: AssetManifest;
  /** The authoritative quote for this item, or null while it loads. */
  readonly quote: UpgradeQuoteWire | null;
  readonly busy: boolean;
  readonly error: string | null;
  /** The outcome of the last attempt, for the line under the button. */
  readonly lastOutcome: string | null;
  readonly currency: UpgradeCurrency;
  readonly onCurrency: (currency: UpgradeCurrency) => void;
  readonly useStabilizer: boolean;
  readonly onUseStabilizer: (value: boolean) => void;
  readonly onEquip: (itemId: string) => void;
  readonly onUnequip: (component: EquipmentComponent) => void;
  readonly onUpgrade: (itemId: string) => void;
}

/** The outcome line, in the language the game speaks. */
const OUTCOME_TEXT: Readonly<Record<string, string>> = Object.freeze({
  succeeded: 'Aprimoramento concluído.',
  failed: 'A tentativa falhou. O item permanece no nível atual.',
  protected: 'A tentativa falhou. O Estabilizador Quântico preservou o item.',
  destroyed: 'A tentativa falhou e o item foi destruído.',
});

/** Why an attempt is impossible, said plainly. */
const BLOCKED_TEXT: Readonly<Record<string, string>> = Object.freeze({
  item_equipped: 'Remova o equipamento antes de aprimorar.',
  item_reserved: 'Reservado em uma negociação. Finalize ou cancele antes de aprimorar.',
  insufficient_funds: 'Saldo insuficiente em Gold e em Cash.',
  equipment_max_level: 'Nível máximo alcançado.',
});

export function EquipmentDetails({
  item,
  manifest,
  quote,
  busy,
  error,
  lastOutcome,
  currency,
  onCurrency,
  useStabilizer,
  onUseStabilizer,
  onEquip,
  onUnequip,
  onUpgrade,
}: EquipmentDetailsProps) {
  if (!item) {
    return (
      <aside className="equip-details" data-testid="equipment-details-empty">
        <p className="inv-state">Selecione um equipamento.</p>
      </aside>
    );
  }

  const stabilizers = quote ? BigInt(quote.stabilizersAvailable) : 0n;
  const canProtect = quote?.destroysOnFailure === true && stabilizers > 0n;
  const cost = quote ? (currency === 'gold' ? quote.goldCost : quote.cashCost) : '0';
  const balance = quote ? (currency === 'gold' ? quote.goldBalance : quote.cashBalance) : '0';
  const affordable = quote ? BigInt(balance) >= BigInt(cost) : false;
  const blocked = quote?.blockedReason ?? null;
  const maxed = item.level >= EQUIPMENT_MAX_LEVEL;
  // A strong confirmation is required for anything that can destroy the item,
  // and the browser's own confirm is deliberately the mechanism: it cannot be
  // clicked through by accident and it cannot be styled into invisibility.
  const upgradeDisabled = busy || !quote || maxed || blocked !== null || !affordable;

  return (
    <aside
      className="equip-details"
      data-paint={rarityPaint(item.rarity)}
      style={rarityStyle(item.rarity)}
      data-testid="equipment-details"
    >
      <header className="equip-details-head">
        {/*
          The component itself, alive.

          A still thumbnail beside a list of numbers reads as a catalogue entry;
          the piece the player is deciding about should look like it is running.
          The motion is a slow float plus a breathing rarity glow on the SAME
          PNG the inventory cell uses - no second asset, no canvas, and no
          second copy of the art to keep in step with the first.
        */}
        {staticAssetUrl(manifest, item.definitionId) ? (
          <span className="equip-details-art" data-testid="equip-details-art" aria-hidden="true">
            <img
              src={staticAssetUrl(manifest, item.definitionId) ?? undefined}
              alt=""
              draggable={false}
            />
          </span>
        ) : null}
        <h3 data-testid="equip-details-name">{item.componentLabel}</h3>
        <p className="equip-details-sub">
          <span data-testid="equip-details-rarity">{rarityLabel(item.rarity)}</span>
          <span data-testid="equip-details-level">Nível {item.level}</span>
          <span data-testid="equip-details-power">
            Power <span className="equip-power-value">{item.power}</span>
          </span>
        </p>
        <p className="equip-details-state" data-testid="equip-details-state">
          {item.equipped ? 'Equipado' : item.locked ? 'Reservado' : 'Disponível'}
        </p>
      </header>

      {/*
        Everything between the header and the footer SCROLLS.

        At 1366x768 the attribute list of a Prismatic plus the quote plus the
        payment controls is taller than the panel, and the first version simply
        overflowed: "Aprimorar" was measured off the bottom of the panel and a
        player could not reach it. The buttons now live in a footer that is
        always on screen, and only the reading material moves.
      */}
      <div className="equip-details-scroll">
        <section className="equip-attributes" aria-label="Atributos">
          <p className="equip-multiplier" data-testid="equip-multiplier">
            Multiplicador do nível: ×{levelMultiplierText(item.level)}
          </p>
          <ul data-testid="equip-attribute-list">
            {item.attributes.map((attribute) => (
              <li key={attribute.attribute} data-testid={`equip-attr-${attribute.attribute}`}>
                <span className="equip-attr-label">
                  {attribute.label}
                  {attribute.guaranteed ? (
                    <span className="equip-attr-guaranteed" data-testid="equip-attr-guaranteed">
                      GARANTIDO
                    </span>
                  ) : null}
                </span>
                {/* Base and effective, side by side: the base never changes, and
                  showing only the effective value would hide what a level is
                  actually worth. */}
                <span className="equip-attr-base">Base {attribute.baseText}</span>
                <span className="equip-attr-effective">{attribute.effectiveText}</span>
              </li>
            ))}
          </ul>
        </section>
        <section className="equip-upgrade" aria-label="Aprimoramento">
          <h4>Aprimoramento</h4>
          {maxed ? (
            <p data-testid="equip-upgrade-maxed">Nível máximo alcançado.</p>
          ) : !quote ? (
            <p data-testid="equip-upgrade-loading">Carregando…</p>
          ) : (
            <>
              <dl className="equip-quote" data-testid="equip-quote">
                <div>
                  <dt>Nível atual</dt>
                  <dd data-testid="equip-quote-current">{quote.currentLevel}</dd>
                </div>
                <div>
                  <dt>Nível pretendido</dt>
                  <dd data-testid="equip-quote-target">{quote.targetLevel}</dd>
                </div>
                <div>
                  <dt>Chance de sucesso</dt>
                  {/* The server's basis points, printed rather than recomputed. */}
                  <dd data-testid="equip-quote-chance">{quote.successBps / 100}%</dd>
                </div>
                <div>
                  <dt>Risco</dt>
                  <dd data-testid="equip-quote-risk">
                    {quote.destroysOnFailure
                      ? useStabilizer && canProtect
                        ? 'Protegido pelo Estabilizador Quântico'
                        : 'DESTRUIÇÃO em caso de falha'
                      : 'Sem risco de destruição'}
                  </dd>
                </div>
                <div>
                  <dt>Custo</dt>
                  <dd data-testid="equip-quote-cost">
                    {cost} {currency === 'gold' ? 'Gold' : 'Cash'}
                  </dd>
                </div>
                <div>
                  <dt>Power estimado</dt>
                  <dd data-testid="equip-quote-power">
                    <span className="equip-power-value">{quote.powerNow}</span> →{' '}
                    <span className="equip-power-value">{quote.powerAfterSuccess}</span>
                  </dd>
                </div>
                <div>
                  <dt>Ganho de Power</dt>
                  {/*
                  Absolute AND relative, because neither alone is decidable: +2000
                  means nothing without knowing whether the item was worth 3000 or
                  30000, and "+18%" means nothing without the size of the step.
                  Both are computed from the server's two numbers and from nothing
                  else - the client never estimates a Power of its own.
                */}
                  {/* The one Power figure that keeps its own colour: a gain is
                    worth saying in the affirmative, and the panel already uses
                    that green for "this goes up". */}
                  <dd data-testid="equip-quote-power-delta">
                    <span className="equip-power-value equip-power-gain">
                      +{quote.powerAfterSuccess - quote.powerNow} (
                      {quote.powerNow > 0
                        ? `+${Math.round(((quote.powerAfterSuccess - quote.powerNow) / quote.powerNow) * 100)}%`
                        : '—'}
                      )
                    </span>
                  </dd>
                </div>
              </dl>

              <fieldset className="equip-currency">
                <legend>Pagamento</legend>
                {/* One currency or the other, never a mix: the two are radio
                  buttons because that is what the rule is. */}
                <label>
                  <input
                    type="radio"
                    name="equip-currency"
                    value="gold"
                    data-testid="equip-currency-gold"
                    checked={currency === 'gold'}
                    onChange={() => onCurrency('gold')}
                  />
                  Gold ({quote.goldCost})
                </label>
                <label>
                  <input
                    type="radio"
                    name="equip-currency"
                    value="cash"
                    data-testid="equip-currency-cash"
                    checked={currency === 'cash'}
                    onChange={() => onCurrency('cash')}
                  />
                  Cash ({quote.cashCost})
                </label>
                <p className="equip-currency-note">
                  O Cash não aumenta a chance, não impede a destruição e não muda o sorteio.
                </p>
              </fieldset>

              <label className="equip-stabilizer">
                <input
                  type="checkbox"
                  data-testid="equip-stabilizer"
                  checked={useStabilizer}
                  // Only meaningful on an attempt that can destroy, and only
                  // offered when the account actually has one.
                  disabled={!quote.destroysOnFailure || stabilizers <= 0n}
                  onChange={(event) => onUseStabilizer(event.target.checked)}
                />
                Usar Estabilizador Quântico ({quote.stabilizersAvailable} disponíveis)
              </label>

              {blocked ? (
                <p className="equip-blocked" role="alert" data-testid="equip-blocked">
                  {BLOCKED_TEXT[blocked] ?? 'Não é possível aprimorar agora.'}
                </p>
              ) : null}
              {!blocked && !affordable ? (
                <p className="equip-blocked" role="alert" data-testid="equip-unaffordable">
                  Saldo insuficiente nesta moeda.
                </p>
              ) : null}
            </>
          )}
        </section>
      </div>

      {/*
        The ACTION BAR: the two decisions, and nothing else.

        Everything a player reads lives above and scrolls. What is pinned here
        is only what they act on, which is what keeps the bar short enough to
        actually stay pinned - the previous version pinned the whole upgrade
        section and the panel could not hold it.
      */}
      <footer className="equip-details-footer">
        {lastOutcome ? (
          <p className="equip-outcome" data-testid="equip-outcome" role="status">
            {OUTCOME_TEXT[lastOutcome] ?? lastOutcome}
          </p>
        ) : null}
        {error ? (
          <p className="equip-error" role="alert" data-testid="equip-error">
            {error}
          </p>
        ) : null}

        <div className="equip-actions">
          {item.equipped ? (
            <button
              type="button"
              data-testid="equip-remove"
              disabled={busy}
              onClick={() => onUnequip(item.component)}
            >
              Remover
            </button>
          ) : (
            <button
              type="button"
              data-testid="equip-equip"
              disabled={busy || item.locked}
              onClick={() => onEquip(item.itemId)}
            >
              Equipar
            </button>
          )}

          {maxed || !quote ? null : (
            <button
              type="button"
              data-testid="equip-upgrade"
              disabled={upgradeDisabled}
              onClick={() => {
                /*
                 * The strong confirmation, and only where it is warranted.
                 *
                 * An attempt that cannot destroy the item is an ordinary
                 * purchase and asking twice would train the player to click
                 * through the dialog that matters.
                 */
                if (quote.destroysOnFailure && !(useStabilizer && canProtect)) {
                  const confirmed = globalThis.confirm(
                    `Esta tentativa pode DESTRUIR o item permanentemente.\n\n` +
                      `Nível ${quote.currentLevel} → ${quote.targetLevel}\n` +
                      `Chance de sucesso: ${quote.successBps / 100}%\n` +
                      `Custo: ${cost} ${currency === 'gold' ? 'Gold' : 'Cash'}\n\n` +
                      `Confirmar?`,
                  );
                  if (!confirmed) {
                    return;
                  }
                }
                onUpgrade(item.itemId);
              }}
            >
              Aprimorar
            </button>
          )}
        </div>
      </footer>
    </aside>
  );
}
